import {treeRelativePath} from "seo/documentLang";

/**
 * The tabs of the Transfers sheet: the files this account SENT, the files it REQUESTED from its contacts
 * (kitecorp/transferify-ui#3140; requests lived on their own Reminders screen until the owner moved them under
 * Transfers, 2026-09-29), and since kitecorp/transferify-ui#3405 every FILE the account holds, sent and
 * received, one row per file.
 *
 * The tab is chosen by the URL, the way every feature sheet is chosen by its path (`/account/admin` opens
 * the operator queue the same way): `/transfers` is Sent and `/transfers/requested` is Requested, so a link,
 * the back button and a reload all land on the tab they named. A nested PATH rather than a query parameter,
 * because the edge function and the client-only shell already serve every path under `/transfers` by its
 * first segment, so the new tab needed no second list anywhere.
 *
 * React-free on purpose (epic #26): a route rule, testable without rendering.
 */

/** The tabs in the order the strip shows them. */
export const TRANSFERS_TABS = ["sent", "requested", "files"] as const;
export type TransfersTab = typeof TRANSFERS_TABS[number];

/** Where the Sent tab lives: the Transfers sheet's own route. */
export const TRANSFERS_ROUTE = "/transfers";

/** Where the Requested tab lives, and where the retired `/reminders` route now sends its old links. */
export const REQUESTED_FILES_ROUTE = "/transfers/requested";

/** Where the Files tab lives (kitecorp/transferify-ui#3405), under the same `/transfers` segment as the others. */
export const FILES_ROUTE = "/transfers/files";

const TAB_PATHS: Record<TransfersTab, string> = {
	sent: TRANSFERS_ROUTE,
	requested: REQUESTED_FILES_ROUTE,
	files: FILES_ROUTE,
};

/**
 * Which tab `pathname` shows. Anything under `/transfers` that is not another tab's path is Sent. Read
 * inside the language tree, so `/de/transfers/requested` is the Requested tab too
 * (kitecorp/transferify-ui#3521); {@link treeRelativePath} also drops a trailing slash.
 */
export function transfersTabFor(pathname: string): TransfersTab {
	const path = treeRelativePath(pathname);
	return TRANSFERS_TABS.find(tab => TAB_PATHS[tab] === path) ?? "sent";
}

/** The path that shows `tab`. */
export function transfersTabPath(tab: TransfersTab): string {
	return TAB_PATHS[tab];
}
